import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FaSearch, FaBan, FaCheckCircle, FaPlus, FaEye, FaEyeSlash, FaList, FaTh, FaHospital, FaMapMarkerAlt, FaPhone, FaUser, FaCalendarAlt } from 'react-icons/fa';
import { BiPencil } from 'react-icons/bi';
import '../../App.css';
import client from '../../api/client';
import { keepDigitsString } from '../../Utils/number';
import { toast } from 'react-toastify';
function AdminDashboard() {
  const navigate = useNavigate();
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showPostingsModal, setShowPostingsModal] = useState(false);
  const [selectedHospitalId, setSelectedHospitalId] = useState(null);
  const [postingsFilter, setPostingsFilter] = useState('all');
  const [showAddHospitalModal, setShowAddHospitalModal] = useState(false);
  const [showEditHospitalModal, setShowEditHospitalModal] = useState(false);
  const [editingHospitalId, setEditingHospitalId] = useState(null);
  const [hospitalFormData, setHospitalFormData] = useState({
    hospitalName: '',
    contactPersonName: '',
    contactPersonRole: '',
    address: '',
    pincode: '',
    mobileNumber: '',
    landlineNumber: '',
    emailId: '',
    hospitalWebsite: '',
    password: '',
    reenterPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [pincodeValidation, setPincodeValidation] = useState({ message: '', isValid: null });
  const [locationName, setLocationName] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [contactError, setContactError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [hospitals, setHospitals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isPostingsLoading, setIsPostingsLoading] = useState(false);
  const [postings, setPostings] = useState([]);
  const [postingsTitleHospital, setPostingsTitleHospital] = useState('');
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [originalContact, setOriginalContact] = useState({ email: "", mobile: "" });
  const [isUpdatingHospital, setIsUpdatingHospital] = useState(false);
  const [isAddingHospital, setIsAddingHospital] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [originalPincode, setOriginalPincode] = useState('');
  const [originalDistrict, setOriginalDistrict] = useState({ idDistrict: null, districtName: '' });

  // Format date for display (DD MMM YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getStatusBadgeClass = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'approved':
      case 'assigned':
      case 'selected':
        return 'badge bg-success';
      case 'pending':
      case 'open':
        return 'badge bg-primary';
      case 'suspended':
        return 'badge bg-warning text-dark';
      case 'rejected':
      case 'deleted':
        return 'badge bg-danger text-dark';
      case 'completed':
        return 'badge bg-info text-dark';
      default:
        return 'badge bg-secondary';
    }
  };

  const fetchHospitals = async () => {
    setIsLoading(true);
    setLoadError('');
    try {

      const res = await client.get('/api/hospitals/list-with-postings');
      const list = res.data?.data || [];
      setHospitals(list);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Unable to load hospitals.');
      setHospitals([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  const resetValidationState = () => {
    setContactError('');
    setEmailError('');
    setMobileError('');
    setIsValidatingContact(false);

    setPincodeValidation({ message: '', isValid: null });
    setIsValidatingPincode(false);

    setLocationName('');
    setIdDistrict(null);
  };

  const getHospitalStatus = (hospital) => {
    // Normalize status
    const status = String(hospital.status || '').toLowerCase();
    if (status === 'approved') return 'Approved';
    if (status === 'suspended') return 'Suspended';
    if (status === 'rejected') return 'Rejected';
    if (status === 'pending' || status === '' || !status) return 'Pending';
    // Return capitalized version of status
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const uniqueStatuses = ['All', 'Pending', 'Approved', 'Suspended', 'Rejected'];

  const filteredHospitals = useMemo(() => {
    let filtered = hospitals;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(h => (h.hospitalName || '').toLowerCase().includes(term));
    }

    // Filter by status
    if (statusFilter && statusFilter !== 'All') {
      filtered = filtered.filter(h => getHospitalStatus(h) === statusFilter);
    }

    return filtered;
  }, [hospitals, searchTerm, statusFilter]);

  // Handle search
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
  };

  // Handle view hospital details
  const handleViewHospital = async (hospitalId) => {
    setSelectedHospital(null);
    setIsViewLoading(true);
    setViewError('');
    setShowHospitalModal(true);
    try {
      const res = await client.get(`/api/hospitals/${hospitalId}/registration-details`);
      const data = res.data?.data;
      if (!data) throw new Error('Details not found');
      
      setSelectedHospital({
        ...data,
        dateRegistered: data.dateRegistered || data.createdAt,
        noOfPostings: data.noOfPostings ?? data.noOfPosting ?? 0,
        completedPostings: data.completedPostings ?? 0,
      });
    } catch (err) {
      setViewError(err?.response?.data?.message || 'Unable to load hospital details.');
    } finally {
      setIsViewLoading(false);
    }
  };

  // Handle close hospital modal
  const handleCloseHospitalModal = () => {
    setShowHospitalModal(false);
    setSelectedHospital(null);
  };

  // Handle view postings
  const handleViewPostings = async (hospitalId, filterType = 'all') => {
    setSelectedHospitalId(hospitalId);
    setPostingsFilter(filterType);
    setShowPostingsModal(true);
    setIsPostingsLoading(true);
    setPostings([]);
    setPostingsTitleHospital(hospitals.find(h => h.hospitalID === hospitalId)?.hospitalName || '');
    try {
      const res = await client.get(`/api/hospitals/${hospitalId}/postings`);
      const list = res.data?.data || [];
      setPostings(list);
    } catch (err) {
      setPostings([]);
    } finally {
      setIsPostingsLoading(false);
    }
  };

  // Handle close postings modal
  const handleClosePostingsModal = () => {
    setShowPostingsModal(false);
    setSelectedHospitalId(null);
    setPostingsFilter('all');
  };

  // Handle add hospital click
  const handleAddHospitalClick = () => {
    resetValidationState();
    setOriginalContact({ email: "", mobile: "" }); // just in case
    setShowAddHospitalModal(true);
  };

  // Validate pincode via API
  const validatePincode = async (pincode) => {
    let message = '';
    if (!pincode || pincode.length !== 6) {
      setPincodeValidation({ message: 'Enter a 6-digit pincode.', isValid: false });
      setLocationName('');
      setIdDistrict(null);
      return { ok: false, message: 'Enter a 6-digit pincode.' };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(`/api/districts/validate-pincode?pincode=${pincode}`);
      const result = response.data?.data;
      if (result?.districtName) {
        setLocationName(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        setPincodeValidation({ message: 'Valid pincode', isValid: true });
        return { ok: true, message: '' };
      }
      message = 'Enter a valid pincode.';
      setPincodeValidation({ message, isValid: false });
      setLocationName('');
      setIdDistrict(null);
      return { ok: false, message };
    } catch (err) {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeValidation({ message, isValid: false });
      setLocationName('');
      setIdDistrict(null);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  // Validate contact via API (field can be 'mobile', 'email', or 'both')
  const validateContact = async (field = 'both') => {
    let message = '';
    if (field === 'both') {
      setContactError('');
    }
    if (field !== 'email') {
      setMobileError('');
    }
    if (field !== 'mobile') {
      setEmailError('');
    }

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(hospitalFormData.emailId);
    const mobileValid = field === 'email' ? true : hospitalFormData.mobileNumber.length === 10;

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        setEmailError('Enter a valid email.');
        message = 'Enter a valid email.';
      }
      if (!mobileValid) {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = message || 'Enter a valid 10-digit mobile number.';
      }
      if (field === 'both' && message) {
        setContactError(message);
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : hospitalFormData.emailId || null,
        mobileNumber: field === 'email' ? null : hospitalFormData.mobileNumber || null
      };
      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setContactError(message);
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch (error) {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') {
        setEmailError(message);
      } else if (field === 'mobile') {
        setMobileError(message);
      } else {
        setContactError(message);
      }
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  // Handle hospital form change
  const handleHospitalFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'password' || name === 'reenterPassword') {
      if (value.length > 20) {
        toast.error('Password cannot exceed 20 characters!');
        return; // stops user from typing more
      }
      setHospitalFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    // Validation for pincode (only numbers, max 6 digits)
    if (name === 'pincode') {
      const numericValue = keepDigitsString(value).slice(0, 6);
      setPincodeValidation({ message: '', isValid: null });
      setHospitalFormData(prev => ({ ...prev, [name]: numericValue }));
      if (numericValue.length !== 6) {
        setLocationName('');
        setIdDistrict(null);
      }
      return;
    }

    // Validation for mobile number (only numbers, max 10 digits)
    if (name === 'mobileNumber') {
      setMobileError('');
      setContactError('');
      const numericValue = keepDigitsString(value).slice(0, 10);
      setHospitalFormData(prev => ({ ...prev, [name]: numericValue }));
      return;
    }
    if (name === 'emailId') {
      setEmailError('');
      setContactError('');
      setHospitalFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    // Validation for landline number (only numbers, max 15 digits)
    if (name === 'landlineNumber') {
      const numericValue = keepDigitsString(value).slice(0, 15);
      setHospitalFormData(prev => ({ ...prev, [name]: numericValue }));
      return;
    }

    setHospitalFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle hospital form submit
  const handleHospitalFormSubmit = async (e) => {
    e.preventDefault();

    if (hospitalFormData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode!');
      return;
    }
    if (hospitalFormData.password !== hospitalFormData.reenterPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    if (hospitalFormData.password.length < 6 || hospitalFormData.password.length > 12) {
      toast.error('Password must be between 6 and 12 characters!');
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(hospitalFormData.emailId.trim())) {
      toast.error('Please enter a valid email ID!');
      return;
    }
    if (hospitalFormData.mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number!');
      return;
    }

    const [mobileResult, emailResult] = await Promise.all([
      validateContact('mobile'),
      validateContact('email')
    ]);
    if (!mobileResult.ok) {
      toast.error(mobileResult.message || 'Please enter a valid mobile number.');
      return;
    }
    if (!emailResult.ok) {
      toast.error(emailResult.message || 'Please enter a valid email ID.');
      return;
    }

    const pinResult = await validatePincode(hospitalFormData.pincode);
    if (!pinResult.ok) {
      toast.error(pinResult.message || 'Please enter a valid pincode.');
      return;
    }

    try {
      setIsAddingHospital(true);
      await client.post('/api/hospitals', {
        EmailID: hospitalFormData.emailId,
        MobileNumber: hospitalFormData.mobileNumber,
        Password: hospitalFormData.password,
        HospitalName: hospitalFormData.hospitalName,
        Address: hospitalFormData.address,
        IdDistrict: idDistrict ?? 0,
        Pincode: hospitalFormData.pincode,
        ContactPersonName: hospitalFormData.contactPersonName,
          ContactPersonRole: hospitalFormData.contactPersonRole || null,
        LandlineNumber: hospitalFormData.landlineNumber || null,
        Website: hospitalFormData.hospitalWebsite || null,
        IsSuspended: false,
      });
      toast.success("Hospital added successfully!");
      handleCloseAddHospitalModal();
      fetchHospitals();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add hospital.");
    } finally {
      setIsAddingHospital(false);
    }
  };

  // Handle close add hospital modal
  const handleCloseAddHospitalModal = () => {
    setShowAddHospitalModal(false);
    resetValidationState();
    setHospitalFormData({
      hospitalName: '',
      contactPersonName: '',
      contactPersonRole: '',
      address: '',
      pincode: '',
      mobileNumber: '',
      landlineNumber: '',
      emailId: '',
      hospitalWebsite: '',
      password: '',
      reenterPassword: ''
    });
    setPincodeValidation({ message: '', isValid: null });
    setLocationName('');
    setShowPassword(false);
    setShowReenterPassword(false);
  };

  // Handle edit hospital
  const handleEditHospital = async (hospitalId) => {
    resetValidationState();
    setEditingHospitalId(hospitalId);
    try {
      const res = await client.get(`/api/hospitals/${hospitalId}/edit`);
      const data = res.data?.data;
      if (!data) throw new Error('Hospital not found');
      setHospitalFormData({
        hospitalName: data.hospitalName || '',
        contactPersonName: data.contactPersonName || '',
        contactPersonRole: data.contactPersonRole || data.ContactPersonRole || '',
        address: data.address || '',
        pincode: data.pincode || '',
        mobileNumber: data.mobileNumber || '',
        landlineNumber: data.landlineNumber || '',
        emailId: data.emailID || '',
        hospitalWebsite: data.hospitalWebsite || '',
        password: '',
        reenterPassword: ''
      });
      setOriginalContact({
        email: data.emailID || "",
        mobile: data.mobileNumber || ""
      });
      setOriginalPincode(data.pincode || '');
      const districtName =
        data.districtName ||
        data.district?.districtName ||
        data.district?.distrinctName ||
        '';

      setOriginalDistrict({
        idDistrict: data.idDistrict ?? null,
        districtName
      });

      setLocationName(districtName);
      setIdDistrict(data.idDistrict ?? null);

      // optional: show green valid state initially
      setPincodeValidation({ message: '', isValid: data.pincode ? true : null });
      setShowPassword(false);
      setShowReenterPassword(false);
      setShowEditHospitalModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load hospital for edit.');
      setEditingHospitalId(null);
    }
  };

  // Handle close edit hospital modal
  const handleCloseEditHospitalModal = () => {
    resetValidationState();
    setShowEditHospitalModal(false);
    setEditingHospitalId(null);
    setHospitalFormData({
      hospitalName: '',
      contactPersonName: '',
      contactPersonRole: '',
      address: '',
      pincode: '',
      mobileNumber: '',
      landlineNumber: '',
      emailId: '',
      hospitalWebsite: '',
      password: '',
      reenterPassword: ''
    });
    setOriginalContact({ email: "", mobile: "" });
    setPincodeValidation({ message: '', isValid: null });
    setLocationName('');
    setShowPassword(false);
    setShowReenterPassword(false);
  };

  // Handle edit hospital form submit
  const handleEditHospitalFormSubmit = async (e) => {
    e.preventDefault();

    if (hospitalFormData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode!');
      return;
    }
    if (hospitalFormData.password || hospitalFormData.reenterPassword) {
      if (hospitalFormData.password !== hospitalFormData.reenterPassword) {
        toast.error('Passwords do not match!');
        return;
      }
      if (hospitalFormData.password.length < 6 || hospitalFormData.password.length > 12) {
        toast.error('Password must be between 6 and 12 characters!');
        return;
      }
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(hospitalFormData.emailId.trim())) {
      toast.error('Please enter a valid email ID!');
      return;
    }
    if (hospitalFormData.mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number!');
      return;
    }

    const emailChanged =
      (hospitalFormData.emailId || "").trim().toLowerCase() !==
      (originalContact.email || "").trim().toLowerCase();

    const mobileChanged =
      (hospitalFormData.mobileNumber || "").trim() !==
      (originalContact.mobile || "").trim();

    if (emailChanged) {
      const emailResult = await validateContact("email");
      if (!emailResult.ok) {
        toast.error(emailResult.message || "Please enter a valid email ID.");
        return;
      }
    }

    if (mobileChanged) {
      const mobileResult = await validateContact("mobile");
      if (!mobileResult.ok) {
        toast.error(mobileResult.message || "Please enter a valid mobile number.");
        return;
      }
    }

    const curPin = (hospitalFormData.pincode || '').trim();
    const origPin = (originalPincode || '').trim();
    const pincodeChanged = curPin !== origPin;

    if (pincodeChanged) {
      const pinResult = await validatePincode(curPin);
      if (!pinResult.ok) {
        toast.error(pinResult.message || 'Please enter a valid pincode.');
        return;
      }
    } else {
      // ensure we still have district for payload if pincode not changed
      setLocationName(originalDistrict.districtName || '');
      setIdDistrict(originalDistrict.idDistrict ?? null);
    }
    try {
      setIsUpdatingHospital(true);
      await client.put('/api/hospitals', {
        HospitalID: editingHospitalId,
        HospitalName: hospitalFormData.hospitalName,
        Address: hospitalFormData.address,
        IdDistrict: idDistrict ?? originalDistrict.idDistrict ?? 0,
        Pincode: hospitalFormData.pincode,
        ContactPersonName: hospitalFormData.contactPersonName,
        ContactPersonRole: hospitalFormData.contactPersonRole || null,
        Password: hospitalFormData.password || null,
        EmailID: hospitalFormData.emailId,
        MobileNumber: hospitalFormData.mobileNumber,
        LandlineNumber: hospitalFormData.landlineNumber || null,
        Website: hospitalFormData.hospitalWebsite || null,
        IsSuspended: false,
      });
      toast.success("Hospital updated successfully!");
      handleCloseEditHospitalModal();
      fetchHospitals();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update hospital.");
    } finally {
      setIsUpdatingHospital(false);
    }
  };

  // Character counter
  const addressCount = hospitalFormData.address.length;

  // Password validation
  const passwordsMatch = hospitalFormData.password === hospitalFormData.reenterPassword && hospitalFormData.reenterPassword !== '';

  // Handle update hospital status
  const handleUpdateHospitalStatus = async (status) => {
    if (!selectedHospital || !selectedHospital.hospitalID) {
      toast.error('Hospital information not available.');
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const response = await client.post('/api/hospitals/status', {
        HospitalID: selectedHospital.hospitalID,
        Status: status
      });

      if (response.status === 200) {
        toast.success(`Hospital status updated to ${status} successfully!`);

        // Update the selected hospital status in the modal
        setSelectedHospital(prev => ({
          ...prev,
          status: status
        }));

        // Refresh the hospital list
        await fetchHospitals();
      } else {
        toast.error('Failed to update hospital status.');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to update hospital status. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title mb-0">List of Hospitals</h2>
                <div className="d-block d-md-flex justify-content-between align-items-center">
                  {/* Table/Grid View Toggle */}
                  <div className="btn-group me-3" role="group" aria-label="View Toggle">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddHospitalClick}
                  >
                    <FaPlus className="me-1" /> Add Hospital
                  </button>
                </div>
              </div>

              {/* Search Section */}
              <div className="mb-4">
                <div className="row align-items-end">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="search" className="form-label">
                      Search
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FaSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="search"
                        placeholder="Search by Hospital Name"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="statusFilter" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="statusFilter"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      {uniqueStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>


              {/* Hospitals Table View */}
              {viewMode === 'table' && (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Sl#</th>
                            <th scope="col">Name of Hospital</th>
                            <th scope="col">Date Registered</th>
                            <th scope="col">Address</th>
                            <th scope="col">Mobile No.</th>
                            <th scope="col">Landline No.</th>
                            <th scope="col" className="text-center">No of Posting</th>
                            <th scope="col" className="text-center">Completed Postings</th>
                            <th scope="col">Status</th>
                            <th scope="col" style={{ width: "110px" }} className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="10" className="text-center">Loading...</td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td colSpan="10" className="text-center text-danger">{loadError}</td>
                            </tr>
                          ) : filteredHospitals.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center">No hospitals found</td>
                            </tr>
                          ) : (
                            filteredHospitals.map((hospital, index) => (
                              <tr key={hospital.hospitalID}>
                                <td>{index + 1}</td>
                                <td>
                                    <div className='fw-bold'>
                                      {hospital.hospitalName}
                                    </div>
                                    <small className="text-muted">{hospital.contactPersonName}{hospital.contactPersonRole ? ` - ${hospital.contactPersonRole}` : ''}</small>
                                </td>
                                <td>{formatDate(hospital.createdAt)}</td>
                                <td>
                                  {hospital.address}
                                  <br />
                                  <small className="text-muted">Pincode: {hospital.pincode}</small>
                                </td>
                                <td>{hospital.mobileNumber}</td>
                                <td>{hospital.landlineNumber}</td>
                                <td className="text-center">
                                  <a
                                    href="#"
                                    className="text-primary text-decoration-none"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewPostings(hospital.hospitalID, 'all');
                                    }}
                                  >
                                    {hospital.noOfPosting ?? hospital.noOfPostings ?? 0}
                                  </a>
                                </td>
                                <td className="text-center">
                                  <a
                                    href="#"
                                    className="text-primary text-decoration-none"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewPostings(hospital.hospitalID, 'completed');
                                    }}
                                  >
                                    {hospital.completedPostings ?? 0}
                                  </a>
                                </td>
                                <td>
                                  <span className={`badge ${getStatusBadgeClass(getHospitalStatus(hospital))}`}>
                                    {getHospitalStatus(hospital)}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-primary p-1 px-2"
                                      title="Edit hospital details"
                                      onClick={() => handleEditHospital(hospital.hospitalID)}
                                    >
                                      <BiPencil />
                                    </button>

                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-primary p-1 px-2"
                                      title="View hospital details"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewHospital(hospital.hospitalID);
                                      }}
                                    >
                                      <FaEye />
                                    </button>
                                  </div>
                                </td>

                              </tr>

                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Hospitals Grid View */}
              {viewMode === 'grid' && (
                <div className="row g-3">
                  {filteredHospitals.length === 0 ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">No hospitals found</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    filteredHospitals.map((hospital) => (
                      <div key={hospital.hospitalID} className="col-12 col-md-6 col-lg-4">
                        <div className="card h-100 opportunity-card modern-card">
                          <div className="card-body d-flex flex-column p-0">
                            {/* Header Section */}
                            <div className="card-header-section">
                              <h5 className="card-position-title">
                                <a
                                  href="#"
                                  className="text-dark text-decoration-none"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewHospital(hospital.hospitalID);
                                  }}
                                >
                                  {hospital.hospitalName}
                                </a>
                              </h5>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`badge ${getStatusBadgeClass(getHospitalStatus(hospital))}`}>
                                  {getHospitalStatus(hospital)}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-link btn-outline-primary p-1 px-2"
                                  title="Edit hospital details"
                                  onClick={() => handleEditHospital(hospital.hospitalID)}
                                >
                                  <BiPencil />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link btn-outline-primary p-1 px-2"
                                  title="View hospital details"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewHospital(hospital.hospitalID);
                                  }}
                                >
                                  <FaEye />
                                </button>
                              </div>
                            </div>

                            {/* Info Section */}
                            <div className="card-info-section">
                              <div className="info-item">
                                <div className="info-icon hospital-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Date Registered</div>
                                  <div className="info-value">{formatDate(hospital.createdAt)}</div>
                                </div>
                              </div>

                              <div className="info-item">
                                <div className="info-icon location-icon">
                                  <FaMapMarkerAlt />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Address</div>
                                  <div className="info-value">
                                    {hospital.address}
                                    <br />
                                    <small className="text-muted">Pincode: {hospital.pincode}</small>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Details Section */}
                            <div className="card-details-section">
                              <div className="detail-item">
                                <FaUser className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Contact Person</span>
                                  <span className="detail-value">{hospital.contactPersonName}{hospital.contactPersonRole ? ` - ${hospital.contactPersonRole}` : ''}</span>
                                </div>
                              </div>

                              <div className="detail-item">
                                <FaPhone className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Mobile</span>
                                  <span className="detail-value">{hospital.mobileNumber}</span>
                                </div>
                              </div>

                              <div className="detail-item">
                                <FaHospital className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Total Postings</span>
                                  <span className="detail-value">
                                    <a
                                      href="#"
                                      className="text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPostings(hospital.hospitalID, 'all');
                                      }}
                                    >
                                      {hospital.noOfPosting ?? hospital.noOfPostings ?? 0}
                                    </a>
                                  </span>
                                </div>
                              </div>

                              <div className="detail-item">
                                <FaCheckCircle className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Completed</span>
                                  <span className="detail-value">
                                    <a
                                      href="#"
                                      className="text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPostings(hospital.hospitalID, 'completed');
                                      }}
                                    >
                                      {hospital.completedPostings ?? 0}
                                    </a>
                                  </span>
                                </div>
                              </div>
                            </div>


                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

      {/* Hospital Details Modal */}
      <Modal
        show={showHospitalModal}
        onHide={handleCloseHospitalModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Hospital Registration Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isViewLoading && (
            <div className="text-center py-4 text-muted">Loading...</div>
          )}
          {(!isViewLoading && viewError) && (
            <div className="text-center py-4 text-danger">{viewError}</div>
          )}
          {(!isViewLoading && !viewError && selectedHospital) && (
            <div className="row m-0">
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Name of Hospital</label>
                <p>{selectedHospital.hospitalName || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Contact Person Name </label>
                <p>{selectedHospital.contactPersonName || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Contact Person Role</label>
                <p>{selectedHospital.contactPersonRole || selectedHospital.contactPersonRole || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Address</label>
                <p>{selectedHospital.address || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Pincode</label>
                <p>
                  {selectedHospital.pincode || '-'}
                  {selectedHospital.districtName && (
                    <span className="text-muted ms-2">({selectedHospital.districtName})</span>
                  )}
                </p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Mobile Number </label>
                <p>{selectedHospital.mobileNumber || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Landline Number </label>
                <p>{selectedHospital.landlineNumber || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Email ID</label>
                <p>{selectedHospital.emailID || selectedHospital.emailId || '-'}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Hospital Website</label>
                <p>
                  {selectedHospital.hospitalWebsite ? (
                    <a href={selectedHospital.hospitalWebsite} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                      {selectedHospital.hospitalWebsite}
                    </a>
                  ) : '-'}
                </p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Date Registered</label>
                <p>{formatDate(selectedHospital.dateRegistered)}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Status</label>
                <p>
                  <span className={`badge ${getStatusBadgeClass(getHospitalStatus(selectedHospital))}`}>
                    {getHospitalStatus(selectedHospital)}
                  </span>
                </p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">No of Postings</label>
                <p>
                  <a
                    href="#"
                    className="text-primary text-decoration-none fw-bold"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCloseHospitalModal();
                      handleViewPostings(selectedHospital.hospitalID, 'all');
                    }}
                  >
                    {selectedHospital.noOfPostings || selectedHospital.noOfPosting || 0}
                  </a>
                </p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label fw-bold mb-1">Completed Postings</label>
                <p>
                  <a
                    href="#"
                    className="text-primary text-decoration-none fw-bold"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCloseHospitalModal();
                      handleViewPostings(selectedHospital.hospitalID, 'completed');
                    }}
                  >
                    {selectedHospital.completedPostings || 0}
                  </a>
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedHospital && (
            <>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleUpdateHospitalStatus('Approved')}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Approve'}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => handleUpdateHospitalStatus('Suspended')}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Suspend'}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleUpdateHospitalStatus('Rejected')}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Reject'}
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseHospitalModal}
            disabled={isUpdatingStatus}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Hospital Postings Modal */}
      <Modal
        show={showPostingsModal}
        onHide={handleClosePostingsModal}
        centered
        size="xl"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Hospital Postings
            {selectedHospitalId && (
              <span className="ms-2 text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                - {postingsTitleHospital || hospitals.find(h => h.hospitalID === selectedHospitalId)?.hospitalName}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHospitalId && (
            <>
              {isPostingsLoading ? (
                <div className="text-center py-4 text-muted">Loading...</div>
              ) : postings.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No postings found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Sl#</th>
                        <th scope="col">Requirement</th>
                        <th scope="col">Posted Date</th>
                        <th scope="col">Period</th>
                        <th scope="col">Status</th>
                        <th scope="col">Responses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postings
                        .filter(p => postingsFilter === 'all'
                          ? true
                          : String(p.status || '').toLowerCase() === 'completed' || String(p.status || '').toLowerCase() === 'selected')
                        .map((posting, index) => (
                          <tr key={posting.jobID || posting.id || index}>
                            <td>{index + 1}</td>
                            <td>{posting.requirement || posting.professionTypeName || '-'}</td>
                            <td>{formatDate(posting.createdAt)}</td>
                            <td>{posting.period || '-'}</td>
                            <td>
                              <span className={getStatusBadgeClass(posting.status || '')}>
                                {posting.status || '-'}
                              </span>
                            </td>
                            <td>{posting.responses ?? 0}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClosePostingsModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Add Hospital Modal */}
      <Modal
        show={showAddHospitalModal}
        onHide={handleCloseAddHospitalModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Add Hospital</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleHospitalFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              {/* Name of Hospital */}
              <div className="col-md-6 p-2">
                <label htmlFor="hospitalName" className="form-label">
                  Name of Hospital <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="hospitalName"
                  name="hospitalName"
                  value={hospitalFormData.hospitalName}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital name"
                  maxLength="60"
                  required
                />
              </div>

              {/* Contact Person Name */}
              <div className="col-md-6 p-2">
                <label htmlFor="contactPersonName" className="form-label">
                  Contact Person Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="contactPersonName"
                  name="contactPersonName"
                  value={hospitalFormData.contactPersonName}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter contact person name"
                  maxLength="60"
                  required
                />
              </div>

              {/* Contact Person Role */}
              <div className="col-md-6 p-2">
                <label htmlFor="contactPersonRole" className="form-label">
                  Contact Person Role
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="contactPersonRole"
                  name="contactPersonRole"
                  value={hospitalFormData.contactPersonRole}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter contact person role"
                  maxLength="100"
                />
              </div>

              {/* Address */}
              <div className="col-md-6 p-2">
                <label htmlFor="address" className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  id="address"
                  name="address"
                  rows="3"
                  value={hospitalFormData.address}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital address"
                  maxLength="200"
                  required
                />
                <small className="form-text text-muted">
                  <span id="addressCount">{addressCount}</span>/200 characters
                </small>
              </div>

              {/* Pincode */}
              <div className="col-md-6 p-2">
                <label htmlFor="pincode" className="form-label">
                  Pincode <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`form-control w-25 ${pincodeValidation.isValid === true ? 'is-valid' : pincodeValidation.isValid === false ? 'is-invalid' : ''}`}
                    id="pincode"
                    name="pincode"
                    value={hospitalFormData.pincode}
                    onChange={handleHospitalFormChange}
                    onBlur={() => validatePincode(hospitalFormData.pincode)}
                    placeholder="Pincode"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    required
                  />
                  <input
                    type="text"
                    className="form-control w-75 form-control-display-only"
                    id="location"
                    name="location"
                    value={locationName}
                    placeholder="Location"
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </div>
                {pincodeValidation.message && (
                  <small className={`form-text ${pincodeValidation.isValid ? 'text-success' : 'text-danger'}`}>
                    {pincodeValidation.message}
                  </small>
                )}
                {isValidatingPincode && (
                  <small className="form-text text-muted">Validating pincode...</small>
                )}
              </div>

              {/* Mobile Number */}
              <div className="col-md-6 p-2">
                <label htmlFor="mobileNumber" className="form-label">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={hospitalFormData.mobileNumber}
                  onChange={handleHospitalFormChange}
                  onBlur={() => showAddHospitalModal && validateContact('mobile')}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  required
                />
                {mobileError && <small className="form-text text-danger">{mobileError}</small>}
                {isValidatingContact && !emailError && !mobileError && (
                  <small className="form-text text-muted">Validating contact...</small>
                )}
              </div>

              {/* Landline Number */}
              <div className="col-md-6 p-2">
                <label htmlFor="landlineNumber" className="form-label">
                  Landline Number <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  id="landlineNumber"
                  name="landlineNumber"
                  value={hospitalFormData.landlineNumber}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter landline number"
                  maxLength="15"
                  required
                />
              </div>

              {/* Email ID */}
              <div className="col-md-6 p-2">
                <label htmlFor="emailId" className="form-label">
                  Email ID <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="emailId"
                  name="emailId"
                  value={hospitalFormData.emailId}
                  onChange={handleHospitalFormChange}
                  onBlur={() => showAddHospitalModal && validateContact('email')}
                  placeholder="Enter email ID"
                  maxLength="50"
                  required
                />
                {emailError && <small className="form-text text-danger">{emailError}</small>}
                {isValidatingContact && !emailError && !mobileError && (
                  <small className="form-text text-muted">Validating contact...</small>
                )}
              </div>

              {/* Hospital Website */}
              <div className="col-md-12 p-2">
                <label htmlFor="hospitalWebsite" className="form-label">
                  Hospital Website
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="hospitalWebsite"
                  name="hospitalWebsite"
                  value={hospitalFormData.hospitalWebsite}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital website URL"
                  maxLength="60"
                />
              </div>

              {/* Password */}
              <div className="col-md-6 p-2">
                <label htmlFor="password" className="form-label">
                  Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    id="password"
                    name="password"
                    value={hospitalFormData.password}
                    onChange={handleHospitalFormChange}
                    placeholder="Enter password (6-20 characters)"
                    minLength="6"
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small className="form-text text-muted">Password must be 6-20 characters long</small>
              </div>

              {/* Re-enter Password */}
              <div className="col-md-6 p-2">
                <label htmlFor="reenterPassword" className="form-label">
                  Re-enter Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type={showReenterPassword ? 'text' : 'password'}
                    className={`form-control ${hospitalFormData.reenterPassword && !passwordsMatch ? 'is-invalid' : hospitalFormData.reenterPassword && passwordsMatch ? 'is-valid' : ''}`}
                    id="reenterPassword"
                    name="reenterPassword"
                    value={hospitalFormData.reenterPassword}
                    onChange={handleHospitalFormChange}
                    placeholder="Re-enter password"
                    minLength="6"
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowReenterPassword(!showReenterPassword)}
                  >
                    {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {hospitalFormData.reenterPassword && !passwordsMatch && (
                  <div className="invalid-feedback d-block">Passwords do not match</div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseAddHospitalModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isAddingHospital}>
              {isAddingHospital ? "Adding..." : "Add Hospital"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit Hospital Modal */}
      <Modal
        show={showEditHospitalModal}
        onHide={handleCloseEditHospitalModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Edit Hospital</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleEditHospitalFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              {/* Name of Hospital */}
              <div className="col-md-6 p-2">
                <label htmlFor="editHospitalName" className="form-label">
                  Name of Hospital <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editHospitalName"
                  name="hospitalName"
                  value={hospitalFormData.hospitalName}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital name"
                  maxLength="60"
                  required
                />
              </div>

              {/* Contact Person Name */}
              <div className="col-md-6 p-2">
                <label htmlFor="editContactPersonName" className="form-label">
                  Contact Person Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editContactPersonName"
                  name="contactPersonName"
                  value={hospitalFormData.contactPersonName}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter contact person name"
                  maxLength="60"
                  required
                />
              </div>

              {/* Contact Person Role */}
              <div className="col-md-6 p-2">
                <label htmlFor="editContactPersonRole" className="form-label">
                  Contact Person Role
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="editContactPersonRole"
                  name="contactPersonRole"
                  value={hospitalFormData.contactPersonRole}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter contact person role (max 100 characters)"
                  maxLength="100"
                />
              </div>

              {/* Address */}
              <div className="col-md-6 p-2">
                <label htmlFor="editAddress" className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  id="editAddress"
                  name="address"
                  rows="3"
                  value={hospitalFormData.address}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital address"
                  maxLength="200"
                  required
                />
                <small className="form-text text-muted">
                  <span id="editAddressCount">{addressCount}</span>/200 characters
                </small>
              </div>

              {/* Pincode */}
              <div className="col-md-6 p-2">
                <label htmlFor="editPincode" className="form-label">
                  Pincode <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`form-control w-25 no-valid-icon ${pincodeValidation.isValid === true
                        ? "is-valid"
                        : pincodeValidation.isValid === false
                          ? "is-invalid"
                          : ""
                      }`}
                    id="editPincode"
                    name="pincode"
                    value={hospitalFormData.pincode}
                    onChange={handleHospitalFormChange}
                    onBlur={() => {
                      if (!showEditHospitalModal) return;

                      const cur = (hospitalFormData.pincode || "").trim();
                      const orig = (originalPincode || "").trim();

                      if (cur.length === 6 && cur !== orig) {
                        validatePincode(cur);
                      } else if (cur === orig) {
                        setLocationName(originalDistrict.districtName || "");
                        setIdDistrict(originalDistrict.idDistrict ?? null);
                        setPincodeValidation({ message: "", isValid: true });
                      }
                    }}
                    placeholder="Pincode"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    required
                  />

                  <input
                    type="text"
                    className="form-control w-75 form-control-display-only"
                    id="editLocation"
                    name="location"
                    value={locationName}
                    placeholder="Location"
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </div>
                {pincodeValidation.message && (
                  <small className={`form-text ${pincodeValidation.isValid ? 'text-success' : 'text-danger'}`}>
                    {pincodeValidation.message}
                  </small>
                )}
                {isValidatingPincode && (
                  <small className="form-text text-muted">Validating pincode...</small>
                )}
              </div>

              {/* Mobile Number */}
              <div className="col-md-6 p-2">
                <label htmlFor="editMobileNumber" className="form-label">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  id="editMobileNumber"
                  name="mobileNumber"
                  value={hospitalFormData.mobileNumber}
                  onChange={handleHospitalFormChange}
                  onBlur={() => {
                    if (!showEditHospitalModal) return;
                    const current = (hospitalFormData.mobileNumber || "").trim();
                    const original = (originalContact.mobile || "").trim();
                    if (current && current !== original) validateContact("mobile");
                  }}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  required
                />
                {mobileError && <small className="form-text text-danger">{mobileError}</small>}
                {isValidatingContact && !emailError && !mobileError && (
                  <small className="form-text text-muted">Validating contact...</small>
                )}
              </div>
              {/* Landline Number */}
              <div className="col-md-6 p-2">
                <label htmlFor="editLandlineNumber" className="form-label">
                  Landline Number <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  id="editLandlineNumber"
                  name="landlineNumber"
                  value={hospitalFormData.landlineNumber}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter landline number"
                  maxLength="15"
                  required
                />
              </div>

              {/* Email ID */}
              <div className="col-md-6 p-2">
                <label htmlFor="editEmailId" className="form-label">
                  Email ID <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control form-control-display-only"
                  id="editEmailId"
                  name="emailId"
                  value={hospitalFormData.emailId}
                  onChange={handleHospitalFormChange}
                  onBlur={() => {
                    const current = (hospitalFormData.emailId || "").trim().toLowerCase();
                    const original = (originalContact.email || "").trim().toLowerCase();
                    if (current && current !== original) validateContact("email");
                  }}
                  placeholder="Enter email ID"
                  maxLength="50"
                  readOnly
                  disabled
                  required
                />
                {emailError && <small className="form-text text-danger">{emailError}</small>}
                {isValidatingContact && !emailError && !mobileError && (
                  <small className="form-text text-muted">Validating contact...</small>
                )}
              </div>

              {/* Hospital Website */}
              <div className="col-md-6 p-2">
                <label htmlFor="editHospitalWebsite" className="form-label">
                  Hospital Website
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="editHospitalWebsite"
                  name="hospitalWebsite"
                  value={hospitalFormData.hospitalWebsite}
                  onChange={handleHospitalFormChange}
                  placeholder="Enter hospital website URL"
                  maxLength="60"
                />
              </div>


            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseEditHospitalModal}
              disabled={isUpdatingHospital}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary px-3"
              disabled={isUpdatingHospital}
            >
              {isUpdatingHospital ? "Updating..." : "Update Hospital"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}

export default AdminDashboard;

